import { isEmpty, min } from 'underscore';
import unescapedText from 'I18n/utils/unescapedText';
import { createSearchRegExp, hasSearchMatch } from 'ContentComponents/components/cmv2/utils/generic';
import { DEFAULT_GOOGLE_FONT_VARIANT_FALLBACK_ORDER } from 'ContentUtils/constants/Fonts';
export const combineFonts = (fontOptions, hasMore = false, input = '') => {
  const {
    googleFontOptions,
    systemFonts,
    customFontOptions = []
  } = fontOptions;
  const searchRegExp = createSearchRegExp(input);
  const combinedSystemAndCustomFonts = [...systemFonts, ...customFontOptions];
  const combinedSystemAndCustomFontsFiltered = combinedSystemAndCustomFonts.filter(font => {
    return hasSearchMatch(searchRegExp, font.text);
  });
  let systemIndex = 0;
  let fontList = [];

  // starts font list if first alphabetical option is a system or custom font
  while (!isEmpty(googleFontOptions) && combinedSystemAndCustomFontsFiltered[systemIndex] && combinedSystemAndCustomFontsFiltered[systemIndex].text < googleFontOptions[0].text) {
    fontList.push(combinedSystemAndCustomFontsFiltered[systemIndex]);
    systemIndex++;
  }

  // alphabetically add in system & custom fonts to the google font list
  googleFontOptions.reduce((combinedFontList, fontOption, index) => {
    const systemOrCustomFont = combinedSystemAndCustomFontsFiltered[systemIndex];
    const previousFont = googleFontOptions[index - 1];
    if (systemOrCustomFont && systemOrCustomFont.text < fontOption.text) {
      if (previousFont && systemOrCustomFont.text > previousFont.text) {
        combinedFontList.push(systemOrCustomFont);
        systemIndex++;
      }
      if (!previousFont) {
        combinedFontList.push(systemOrCustomFont);
        systemIndex++;
      }
    }
    combinedFontList.push(fontOption);
    return combinedFontList;
  }, fontList);

  // finishes font list if last alphabetical option is a system or custom font
  if (!hasMore && combinedSystemAndCustomFontsFiltered.length) {
    fontList = fontList.concat(combinedSystemAndCustomFontsFiltered.slice(systemIndex));
  }
  return fontList;
};
const i18nKey = key => `ContentComponents.cmv2.fields.font.${key}`;
export const formatVariant = variant => {
  const weight = parseInt(variant, 10) || 400;
  let variantText = unescapedText(i18nKey(`weights.${weight}`));
  if (variant.includes('italic')) {
    variantText = unescapedText(i18nKey('weights.italic'), {
      weight: variantText
    });
  }
  return variantText;
};
export const getVariantStyles = variant => {
  const styles = {
    fontWeight: parseInt(variant, 10) || 400
  };
  if (variant.includes('italic')) styles.fontStyle = 'italic';
  return styles;
};
export const getDefaultVariant = (variants = []) => {
  if (!variants.length) return '';
  const stringVariants = typeof variants[0] === 'object' ? variants.map(variant => variant.name) : variants;
  const variantsMap = stringVariants.reduce((map, variant) => {
    map[variant] = variant;
    return map;
  }, {});
  for (const variant of DEFAULT_GOOGLE_FONT_VARIANT_FALLBACK_ORDER) {
    if (variantsMap[variant]) return variant;
  }
  return stringVariants[0];
};
export const parseVariantWeight = variant => {
  const name = typeof variant === 'object' ? variant.name : variant;
  const weight = parseInt(name, 10);
  if (!isNaN(weight)) return weight;
  const weightMap = {
    regular: 400,
    normal: 400,
    bold: 700,
    light: 300,
    thin: 100,
    medium: 500,
    semibold: 600,
    black: 900
  };
  return weightMap[name.toLowerCase()] || 400;
};
const isItalicVariant = variant => {
  const name = typeof variant === 'object' ? variant.name : variant;
  return name.toLowerCase().includes('italic');
};
export const getNearestVariant = (variants, targetVariant) => {
  if (!variants || variants.length === 0) {
    return targetVariant;
  }
  const targetWeight = parseVariantWeight(targetVariant);
  const targetIsItalic = isItalicVariant(targetVariant);
  const variantsMatchingItalic = variants.filter(v => isItalicVariant(v) === targetIsItalic);
  const variantsToSearch = variantsMatchingItalic.length > 0 ? variantsMatchingItalic : variants;
  const closest = min(variantsToSearch, v => Math.abs(parseVariantWeight(v) - targetWeight));
  return typeof closest === 'object' ? closest.name : closest;
};
const fontsCache = {};
const variantsCache = {};
export const loadFontStylesheet = (family, variants, options) => {
  if (!family) {
    return;
  }
  const googleFontUrl = `https://fonts.googleapis.com/css2?family=${family.replace(/\s/g, '+')}`;
  let weights = '';
  if (variants && !variantsCache[family]) {
    variantsCache[family] = family;
    weights = `:wght@${variants.filter(variant => !variant.includes('italic')).map(variant => parseInt(variant, 10) || 400).join(';')}`;
  }
  const {
    onFontLoad,
    useCache = true
  } = options || {};
  const fontIsAlreadyLoaded = Boolean(useCache ? fontsCache[family] : document.querySelector(`[data-font-family="${family}"]`));
  if (fontIsAlreadyLoaded) {
    // The variants tend to load later, so this adds in the weights after
    // the font stylesheet was already added
    if (weights) {
      const linkEl = document.querySelector(`link[href^="${googleFontUrl}"]`);
      linkEl.setAttribute('href', `${googleFontUrl}${weights}`);
    }
    onFontLoad === null || onFontLoad === void 0 || onFontLoad();
    return;
  }
  fontsCache[family] = family;
  const linkEl = document.createElement('link');
  linkEl.setAttribute('href', `${googleFontUrl}${weights}`);
  linkEl.setAttribute('rel', 'stylesheet');
  linkEl.setAttribute('data-font-family', family);
  if (onFontLoad) {
    linkEl.addEventListener('load', onFontLoad);
  }
  document.getElementsByTagName('head')[0].appendChild(linkEl);
};

// TODO - see if we can fetch this from BE instead of recreating font-face logic
// TODO - only include variants that are actually in use
export const getFontFaceDeclaration = customFontData => {
  const {
    variants = [],
    name
  } = customFontData;
  return variants.map(variant => {
    const {
      style = '',
      files = []
    } = variant;
    let fontStyles = '';
    try {
      const fontStylesObj = JSON.parse(style) || {};
      fontStyles = Object.keys(fontStylesObj).reduce((acc, cssProperty) => {
        const cssValue = fontStylesObj[cssProperty];
        if (cssValue) {
          return `${acc}\n${cssProperty}: ${cssValue};`;
        }
        return acc;
      }, '');
    } catch (e) {
      console.error(e);
    }
    const src = files.map(file => {
      const {
        file: fileUrl,
        format
      } = file;
      return `url("${fileUrl}") format("${format}")`;
    }).join(',');
    return `
      @font-face {
        font-family: "${name}";
        src: ${src};
        font-display: swap;
        ${fontStyles}
      }\n
  `;
  }).join('\n');
};

// TODO: Only insert font-face declarations for variants in use
export const loadCustomFontStyle = (customFont, onFontLoad) => {
  const {
    name: fontFamily
  } = customFont || {};
  if (!fontFamily) return;
  if (document.querySelector(`[data-font-family="${fontFamily}"]`)) {
    onFontLoad === null || onFontLoad === void 0 || onFontLoad();
    return;
  }
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-font-family', fontFamily);
  styleEl.textContent = getFontFaceDeclaration(customFont);
  if (onFontLoad) {
    styleEl.addEventListener('load', onFontLoad);
  }
  document.getElementsByTagName('head')[0].appendChild(styleEl);
};